import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Badge } from '@/components/common/Badge';
import { Card } from '@/components/common/Card';
import { Skeleton } from '@/components/common/States';
import { ROUTES } from '@/app/router/routes';
import { portalApi, subjectKey } from '@/services/api/endpoints/portal';
import { queryKeys } from '@/services/api/queryKeys';
import type { AttendanceStatus, DayOfWeek, SubmissionStatus, TimetableEntry } from '@/types/domain';
import type { ChildRef, PortalStudent, PortalSubject } from '@/types/portal';
import { isApiError } from '@/utils/ApiError';
import { formatMoney } from '@/utils/format';

// ------------------------------------------------------------------ dates

function iso(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Today in the reader's own time zone — a school day is local, not UTC. */
export function localToday(): string {
  return iso(new Date());
}

/** A local date `days` before today; used only to prefill a date picker. */
export function localDaysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return iso(date);
}

export const DAYS: DayOfWeek[] = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

const DAY_LABELS: Record<DayOfWeek, string> = {
  MONDAY: 'Mon',
  TUESDAY: 'Tue',
  WEDNESDAY: 'Wed',
  THURSDAY: 'Thu',
  FRIDAY: 'Fri',
  SATURDAY: 'Sat',
  SUNDAY: 'Sun',
};

/** JS counts Sunday as 0; the API names the day. */
export function todayDay(): DayOfWeek {
  const names: DayOfWeek[] = [
    'SUNDAY',
    'MONDAY',
    'TUESDAY',
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
    'SATURDAY',
  ];
  return names[new Date().getDay()] ?? 'MONDAY';
}

// -------------------------------------------------------------- timetable

/** Monday to Friday, plus a weekend day only when something is timetabled on it. */
export function visibleDays(entries: TimetableEntry[]): DayOfWeek[] {
  return DAYS.filter(
    (day) =>
      (day !== 'SATURDAY' && day !== 'SUNDAY') || entries.some((entry) => entry.dayOfWeek === day),
  );
}

/**
 * The periods in the order the server listed them.
 *
 * A student cannot read the bell schedule (PERIOD_VIEW), so period times are not
 * available here; the server returns entries in day-then-period order, and the
 * first appearance of each period is taken as its place in the day.
 */
export function orderedPeriods(entries: TimetableEntry[]): { id: string; name: string }[] {
  const seen = new Map<string, string>();
  for (const day of DAYS) {
    for (const entry of entries) {
      if (entry.dayOfWeek === day && !seen.has(entry.periodId)) {
        seen.set(entry.periodId, entry.periodName ?? 'Period');
      }
    }
  }
  return [...seen.entries()].map(([id, name]) => ({ id, name }));
}

/** One day's entries in period order. */
export function entriesForDay(entries: TimetableEntry[], day: DayOfWeek): TimetableEntry[] {
  const order = orderedPeriods(entries).map((period) => period.id);
  return entries
    .filter((entry) => entry.dayOfWeek === day)
    .sort((a, b) => order.indexOf(a.periodId) - order.indexOf(b.periodId));
}

/**
 * A week as a grid: periods down, days across. Wide screens get the table;
 * a phone gets the same week as one list per day, today first.
 */
export function WeekGrid({
  entries,
  renderCell,
}: {
  entries: TimetableEntry[];
  renderCell: (entry: TimetableEntry) => ReactNode;
}) {
  const days = visibleDays(entries);
  const periods = orderedPeriods(entries);
  const today = todayDay();
  const mobileDays = [...days.filter((day) => day === today), ...days.filter((day) => day !== today)];

  return (
    <>
      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full min-w-[640px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-line">
              <th
                scope="col"
                className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-subtle"
              >
                Period
              </th>
              {days.map((day) => (
                <th
                  key={day}
                  scope="col"
                  className={clsx(
                    'px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider',
                    day === today ? 'text-accent' : 'text-subtle',
                  )}
                >
                  {DAY_LABELS[day]}
                  {day === today && <span className="sr-only"> (today)</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {periods.map((period) => (
              <tr key={period.id}>
                <th scope="row" className="px-3 py-2.5 text-left text-xs font-semibold text-muted">
                  {period.name}
                </th>
                {days.map((day) => {
                  const cell = entries.filter(
                    (entry) => entry.dayOfWeek === day && entry.periodId === period.id,
                  );
                  return (
                    <td
                      key={day}
                      className={clsx('px-3 py-2.5 align-top', day === today && 'bg-accent-soft/40')}
                    >
                      {cell.length === 0 ? (
                        <span className="text-subtle">—</span>
                      ) : (
                        cell.map((entry) => <Fragment key={entry.id}>{renderCell(entry)}</Fragment>)
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="divide-y divide-line sm:hidden">
        {mobileDays.map((day) => {
          const list = entriesForDay(entries, day);
          return (
            <section key={day} className="px-4 py-3">
              <h3
                className={clsx(
                  'text-[11px] font-semibold uppercase tracking-wider',
                  day === today ? 'text-accent' : 'text-subtle',
                )}
              >
                {DAY_LABELS[day]}
                {day === today && ' · today'}
              </h3>
              {list.length === 0 ? (
                <p className="mt-1 text-sm text-muted">No lessons.</p>
              ) : (
                <ul className="mt-1 divide-y divide-line/60">
                  {list.map((entry) => (
                    <li key={entry.id} className="flex gap-3 py-2">
                      <span className="w-20 shrink-0 text-xs font-semibold text-muted">
                        {entry.periodName ?? 'Period'}
                      </span>
                      <span className="min-w-0 flex-1">{renderCell(entry)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>
    </>
  );
}

// ---------------------------------------------------------------- badges

const ATTENDANCE_TONES: Record<AttendanceStatus, 'success' | 'danger' | 'warning' | 'info' | 'neutral'> = {
  PRESENT: 'success',
  ABSENT: 'danger',
  LATE: 'warning',
  EXCUSED: 'info',
  LEAVE: 'neutral',
};

export function AttendanceBadge({ status }: { status: AttendanceStatus }) {
  return (
    <Badge tone={ATTENDANCE_TONES[status]}>
      <span className="h-1.5 w-1.5 bg-current" aria-hidden="true" />
      {status.toLowerCase()}
    </Badge>
  );
}

const SUBMISSION_TONES: Record<SubmissionStatus, 'success' | 'warning' | 'info' | 'neutral'> = {
  NOT_SUBMITTED: 'neutral',
  SUBMITTED: 'success',
  LATE: 'warning',
  RETURNED: 'info',
};

export function SubmissionBadge({ status }: { status?: SubmissionStatus }) {
  const value = status ?? 'NOT_SUBMITTED';
  return (
    <Badge tone={SUBMISSION_TONES[value]}>
      <span className="h-1.5 w-1.5 bg-current" aria-hidden="true" />
      {value.replaceAll('_', ' ').toLowerCase()}
    </Badge>
  );
}

/** A server percentage shown as sent, or a dash when there is nothing to base it on. */
export function percentText(value: number | undefined | null): string {
  return value === undefined || value === null ? '—' : `${value}%`;
}

export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line px-4 py-3">
      <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">{children}</h2>
      {action}
    </div>
  );
}

export function isNotFound(error: unknown): boolean {
  return isApiError(error) && error.status === 404;
}

// ----------------------------------------------------------- summary card

/** The areas a student or parent can open, in the order the nav lists them. */
export const PORTAL_AREAS: { label: string; path: string }[] = [
  { label: 'Attendance', path: ROUTES.myAttendance },
  { label: 'Timetable', path: ROUTES.myTimetable },
  { label: 'Results', path: ROUTES.myResults },
  { label: 'Fees', path: ROUTES.myFees },
  { label: 'Bus', path: ROUTES.myBus },
  { label: 'Library', path: ROUTES.myLibraryAccount },
  { label: 'Assignments', path: ROUTES.myAssignments },
];

/**
 * One student at a glance: class, the last 30 days' attendance and the fee
 * balance, with a way into every area. Used once per child on a parent's home
 * and once on a student's own. Each figure is the server's; a panel that cannot
 * load says so without taking the rest of the card down with it.
 */
export function StudentSummaryCard({
  student,
  subject,
  linkFor,
}: {
  student: PortalStudent | ChildRef;
  subject: PortalSubject;
  linkFor: (path: string) => string;
}) {
  const key = subjectKey(subject);

  const attendance = useQuery({
    queryKey: queryKeys.portal.attendance(key),
    queryFn: () => portalApi.attendance(subject),
  });

  const fees = useQuery({
    queryKey: queryKeys.portal.fees(key),
    queryFn: () => portalApi.fees(subject),
  });

  const relationship = 'relationship' in student ? student.relationship : undefined;
  const balance = fees.data?.balance;
  const summary = attendance.data?.summary;

  return (
    <Card className="flex flex-col">
      <div className="border-b border-line px-4 py-3">
        <h2 className="truncate text-base font-bold text-ink">{student.studentName}</h2>
        <p className="mt-0.5 text-xs text-muted">
          {[student.studentNumber, student.sectionName ?? 'No current class', relationship]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-px bg-line sm:grid-cols-2">
        <div className="bg-surface px-4 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">
            Attendance · last 30 days
          </p>
          {attendance.isPending ? (
            <Skeleton className="mt-2 w-24" />
          ) : attendance.isError ? (
            <p className="mt-1 text-sm text-muted">Unavailable just now.</p>
          ) : summary === undefined ? null : (
            <>
              <p className="mt-1 text-[24px] font-extrabold leading-none tabular-nums text-ink">
                {percentText(summary.attendancePercentage)}
              </p>
              <p className="mt-1.5 text-xs text-muted">
                {summary.recordedDays === 0
                  ? 'Nothing recorded yet'
                  : `${summary.presentDays} present · ${summary.absentDays} absent · ${summary.lateDays} late`}
              </p>
            </>
          )}
        </div>
        <div className="bg-surface px-4 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">
            Fees due
          </p>
          {fees.isPending ? (
            <Skeleton className="mt-2 w-24" />
          ) : fees.isError || balance === undefined ? (
            <p className="mt-1 text-sm text-muted">Unavailable just now.</p>
          ) : (
            <>
              <p className="mt-1 break-words text-[24px] font-extrabold leading-none tabular-nums text-ink">
                {formatMoney(balance.netDue, balance.currencyCode)}
              </p>
              <p
                className={clsx(
                  'mt-1.5 text-xs',
                  balance.overdueAmount > 0 ? 'font-semibold text-danger' : 'text-muted',
                )}
              >
                {balance.overdueAmount > 0
                  ? `${formatMoney(balance.overdueAmount, balance.currencyCode)} overdue`
                  : `${balance.unpaidInvoiceCount} unpaid invoice${balance.unpaidInvoiceCount === 1 ? '' : 's'}`}
              </p>
            </>
          )}
        </div>
      </div>

      <nav aria-label={`${student.studentName}: open`} className="mt-auto border-t border-line px-4 py-3">
        <ul className="flex flex-wrap gap-1.5">
          {PORTAL_AREAS.map((area) => (
            <li key={area.path}>
              <Link
                to={linkFor(area.path)}
                className="inline-flex items-center border border-line px-2.5 py-1 text-xs font-semibold text-ink transition-colors hover:bg-surface-2"
              >
                {area.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </Card>
  );
}
